import deepmerge from 'deepmerge';
import { ThemeOptions } from '@material-ui/core';
import { createTheme, alpha } from '@material-ui/core/styles';

import CerealMediumWoff2 from '../assets/fonts/cereal/Cereal-Medium.woff2';
import { cerealAll } from '../assets/fonts/cereal/Cereal';
import {DarkThemeOptions, LightThemeOptions} from "../Utils/CreateTheme";
import MxGraphStyles from "./BaseThemeOptions/MxGraphStyles";

export const defaultBorderRadius = 0;

const robotoCondensed = {
  fontFamily: 'Roboto Condensed',
  fontStyle: 'normal',
  fontDisplay: 'swap',
  src: `url(${CerealMediumWoff2}) format('woff2')`,
};

const baseCssOptions = {
  a: {
    textDecoration: 'none',
    color: 'inherit',
    '& :visited': {
      textDecoration: 'none',
      color: 'inherit',
    }
  },

  '.react-table': {
    color: 'inherit',
  },

  '.fullscreen': {
    display: 'flex',
    flexGrow: 1,
  },

  '@media print': {
    '@page': {
      size: 'letter',
      margin: '1in',
    },
  }
};

const scrollBarOptions = {
  '::-webkit-scrollbar': {
    width: '10px',
  },
  /* Track */
  '::-webkit-scrollbar-track': {
    background: '#f1f1f1',
  },
  /* Handle */
  '::-webkit-scrollbar-thumb': {
    background: '#888',
    '& :hover': {
      background: '#555',
    }
  },
};

const BaseThemeOptions: ThemeOptions = {
  mixins: {
    toolbar: {
      minHeight: 52,
    }
  },
  shape: {
    borderRadius: defaultBorderRadius,
  },
  props: {
    MuiButton: {
      size: 'small',
    },
    MuiFilledInput: {
      margin: 'dense',
    },
    MuiFormControl: {
      margin: 'dense',
    },
    MuiFormHelperText: {
      margin: 'dense',
    },
    MuiIconButton: {
      size: 'small',
    },
    MuiInputBase: {
      margin: 'dense',
    },
    MuiInputLabel: {
      margin: 'dense',
    },
    MuiListItem: {
      dense: true,
    },
    MuiOutlinedInput: {
      margin: 'dense',
    },
    MuiFab: {
      size: 'small',
    },
    MuiTable: {
      size: 'small',
    },
    MuiTextField: {
      margin: 'dense',
    },
    MuiToolbar: {
      variant: 'dense',
    },
  },
  overrides: {
    MuiIconButton: {
      sizeSmall: {
        // Adjust spacing to reach minimal touch target hitbox
        marginLeft: 4,
        marginRight: 4,
        padding: 12,
      },
    },
    MuiCssBaseline: {
      '@global': {
        '@font-face': [robotoCondensed, ...cerealAll],
        ///...scrollBarOptions,
        ...baseCssOptions,
        ...MxGraphStyles,
      }
    },
    MuiPaper: {
      root: {
        borderRadius: defaultBorderRadius,
      },
      rounded: {
        borderRadius: 'unset',
      },
      elevation2: {
        boxShadow: 'unset',
      },
      elevation3: {
        boxShadow: 'unset',
      },
      elevation4: {
        boxShadow: 'unset',
      },
    },
    MuiDialog: {
      paper: {
        overflowY: 'unset',
        borderRadius: defaultBorderRadius,
      },
    },
    MuiDialogActions: {
      root: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '12px',
        margin: 'unset',
        borderBottomLeftRadius: defaultBorderRadius,
        borderBottomRightRadius: defaultBorderRadius,
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 40,
      },
    },
    MuiDialogContent: {
      root: {
        overflowY: 'auto',
      },
    },
    MuiChip: {
      root: {
        borderRadius: '4px',
      },
    },
    MuiCard: {
      root: {
        transition: 'box-shadow 300ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
        borderRadius: defaultBorderRadius,
      },
    },
    MuiCardActions: {
      root: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '12px',
        margin: 'unset',
        borderBottomLeftRadius: defaultBorderRadius,
        borderBottomRightRadius: defaultBorderRadius,
      },
    },
    MuiAppBar: {
      root: {
        borderRadius: 'unset',
      },
    },
  },
  typography: {
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    fontFamily: [
      'Cereal',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  buttonTypes: {
    create: {
      backgroundColor: '#5ac1a4',
      color: createTheme().palette.getContrastText('#5ac1a4'),
      '&:hover': {
        backgroundColor: '#43917c',
      },
    },
    cancel: {
      backgroundColor: '#ffffff',
      color: createTheme().palette.getContrastText('#ffffff'),
      '&:hover': {
        backgroundColor: '#dfdfdf',
      },
    },
    transfer: {
      backgroundColor: '#ee9838',
      color: createTheme().palette.getContrastText('#ee9838'),
      '&:hover': {
        backgroundColor: '#b76a00',
      },
    },
    remove: {
      backgroundColor: '#ca4830',
      color: createTheme().palette.getContrastText('#ca4830'),
      '&:hover': {
        backgroundColor: '#921107',
      },
    },
  },
};

const lightThemeOptions: LightThemeOptions = {
  palette: {
    type: 'light',
    background: {
      default: '#f0f2f5',
    },
    text: {
      primary: 'rgb(72,72,72)',
      secondary: 'rgb(118,118,118)',
    },
  },
  overrides: {
    MuiDialog: {
      paper: {
        border: `1px solid ${alpha(createTheme(BaseThemeOptions).palette.common.black, 0.15)}`,
      },
    },
    MuiDialogActions: {
      root: {
        borderTop: `1px solid ${alpha(createTheme(BaseThemeOptions).palette.common.black, 0.15)}`,
        background: alpha(createTheme(BaseThemeOptions).palette.common.black, 0.06),
      },
    },
    MuiCard: {
      root: {
        border: `1px solid ${alpha(createTheme(BaseThemeOptions).palette.common.black, 0.15)}`,
      },
    },
    MuiCardActions: {
      root: {
        borderTop: `1px solid ${alpha(createTheme(BaseThemeOptions).palette.common.black, 0.15)}`,
        background: alpha(createTheme(BaseThemeOptions).palette.common.black, 0.06),
      },
    },
  },
};

const darkThemeOptions: DarkThemeOptions = {
  palette: {
    type: 'dark',
    text: {
      primary: 'rgb(194,188,184)',
      secondary: 'rgb(155,149,147)',
    },
    background: {
      default: 'rgb(29,30,34)',
      paper: 'rgb(44,47,52)',
    },
  },
  overrides: {
    MuiDialog: {
      paper: {
        border: `1px solid ${alpha(createTheme(BaseThemeOptions).palette.common.white, 0.15)}`,
      },
    },
    MuiDialogActions: {
      root: {
        borderTop: `1px solid ${alpha(createTheme(BaseThemeOptions).palette.common.white, 0.15)}`,
        background: alpha(createTheme(BaseThemeOptions).palette.common.white, 0.06),
      },
    },
    MuiCard: {
      root: {
        border: `1px solid ${alpha(createTheme(BaseThemeOptions).palette.common.white, 0.15)}`,
      },
    },
    MuiCardActions: {
      root: {
        borderTop: `1px solid ${alpha(createTheme(BaseThemeOptions).palette.common.white, 0.15)}`,
        background: alpha(createTheme(BaseThemeOptions).palette.common.white, 0.06),
      },
    },
  },
};

export const BaseDarkThemeOptions: DarkThemeOptions = deepmerge(BaseThemeOptions, darkThemeOptions) as DarkThemeOptions;
export const BaseLightThemeOptions: LightThemeOptions = deepmerge(BaseThemeOptions, lightThemeOptions) as LightThemeOptions;
